
import React, { useState } from 'react';
import { X, Bell, Mail, MessageCircle, Users, Briefcase, DollarSign, Calendar, Heart, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

const NotificationSettings = ({ onClose }) => {
  const [settings, setSettings] = useState({
    channels: {
      inApp: true,
      email: true,
      slack: false,
    },
    categories: {
      influencerActivity: true,
      campaignMilestones: true,
      financeContracts: true,
      tasksReminders: true,
      relationshipIntelligence: true,
      aiSuggestions: false,
      systemAdmin: true,
    }
  });

  const channelOptions = [
    { id: 'inApp', label: 'In-App Notifications', icon: Bell, description: 'Show notifications within the platform' },
    { id: 'email', label: 'Email Notifications', icon: Mail, description: 'Send notifications to your email' },
    { id: 'slack', label: 'Slack Integration', icon: MessageCircle, description: 'Post notifications to Slack channels' },
  ];

  const categoryOptions = [
    {
      id: 'influencerActivity',
      label: 'Influencer Activity',
      icon: Users,
      description: 'Content submissions, engagement updates, profile changes',
      examples: ['New post submitted', 'Engagement milestone reached']
    },
    {
      id: 'campaignMilestones',
      label: 'Campaign Milestones',
      icon: Briefcase,
      description: 'Campaign deadlines, approvals, and performance alerts',
      examples: ['Campaign deadline approaching', 'Content approved']
    },
    {
      id: 'financeContracts',
      label: 'Finance & Contracts',
      icon: DollarSign,
      description: 'Payment reminders, invoice status, contract renewals',
      examples: ['Invoice overdue', 'Contract expiring soon']
    },
    {
      id: 'tasksReminders',
      label: 'Tasks & Reminders',
      icon: Calendar,
      description: 'Follow-ups, deadlines, and scheduled activities',
      examples: ['Follow-up reminder', 'Task due today']
    },
    {
      id: 'relationshipIntelligence',
      label: 'Relationship Intelligence',
      icon: Heart,
      description: 'Birthdays, anniversaries, engagement suggestions',
      examples: ['Birthday reminder', 'Re-engagement suggestion']
    },
    {
      id: 'aiSuggestions',
      label: 'AI Suggestions',
      icon: Zap,
      description: 'Smart recommendations and insights',
      examples: ['Performance insights', 'Optimization suggestions']
    },
    {
      id: 'systemAdmin',
      label: 'System & Admin',
      icon: Bell,
      description: 'System updates, security alerts, admin notifications',
      examples: ['System maintenance', 'Security update']
    },
  ];

  const handleChannelToggle = (channelId) => {
    setSettings(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channelId]: !prev.channels[channelId]
      }
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setSettings(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryId]: !prev.categories[categoryId]
      }
    }));
  };

  const handleSave = () => {
    console.log('Saving notification settings:', settings);
    // Implementation would save settings to backend
    onClose();
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Notification Settings</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Channels Section */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-3">Notification Channels</h3>
          <div className="space-y-3">
            {channelOptions.map((channel) => {
              const Icon = channel.icon;
              return (
                <div key={channel.id} className="flex items-start space-x-3">
                  <Switch
                    checked={settings.channels[channel.id]}
                    onCheckedChange={() => handleChannelToggle(channel.id)}
                    id={channel.id}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <Icon className="w-4 h-4 text-gray-500" />
                      <label htmlFor={channel.id} className="text-sm font-medium text-gray-900 cursor-pointer">
                        {channel.label}
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {channel.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Categories Section */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-3">Notification Categories</h3>
          <div className="space-y-4">
            {categoryOptions.map((category) => {
              const Icon = category.icon;
              const isEnabled = settings.categories[category.id];
              return (
                <div key={category.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start space-x-3">
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => handleCategoryToggle(category.id)}
                      id={category.id}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <Icon className="w-4 h-4 text-gray-500" />
                        <label htmlFor={category.id} className="text-sm font-medium text-gray-900 cursor-pointer">
                          {category.label}
                        </label>
                        {isEnabled && (
                          <Badge variant="secondary" className="text-xs">
                            On
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mb-2">
                        {category.description}
                      </p>
                      {category.examples && (
                        <div className="text-xs text-gray-500">
                          Examples: {category.examples.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <Button onClick={handleSave} className="flex-1">
            Save Settings
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
